import { useEffect, useRef, useState, useCallback } from 'react'
import { createConsumer } from '@rails/actioncable'

export function useGistWebSocket({ onGistUpdate, onUnauthorized }) {
  const [isConnected, setIsConnected] = useState(false)
  const subscriptionRef = useRef(null)
  const consumerRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const isConnectedRef = useRef(false)
  const isUnauthorizedRef = useRef(false)
  const connectTimeRef = useRef(null)

  const connect = useCallback(() => {
    if (subscriptionRef.current) {
      console.log('[WebSocket] Already connected')
      return
    }

    console.log('[WebSocket] Connecting to GistsSyncChannel...')
    connectTimeRef.current = Date.now() // Track attempt time, not connection time

    consumerRef.current = createConsumer('/cable')

    subscriptionRef.current = consumerRef.current.subscriptions.create('GistsSyncChannel', {
      connected() {
        console.log('[WebSocket] Connected to GistsSyncChannel')
        isConnectedRef.current = true
        setIsConnected(true)
      },

      disconnected() {
        console.log('[WebSocket] Disconnected from GistsSyncChannel')
        isConnectedRef.current = false
        setIsConnected(false)

        // Clear subscription reference so we can reconnect
        subscriptionRef.current = null

        // Check if disconnected quickly after connection attempt (likely unauthorized)
        const attemptDuration = connectTimeRef.current ? Date.now() - connectTimeRef.current : Infinity
        if (attemptDuration < 500) {
          console.error(`[WebSocket] Immediate disconnect detected (${attemptDuration}ms), likely unauthorized`)
          isUnauthorizedRef.current = true
          if (onUnauthorized) {
            onUnauthorized()
          }
          return
        }

        if (isUnauthorizedRef.current) {
          console.log('[WebSocket] Not reconnecting due to unauthorized session')
          return
        }

        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[WebSocket] Attempting to reconnect...')
          connect()
        }, 3000)
      },

      received(data) {
        console.log('[WebSocket] Message received:', data.type, data)

        switch (data.type) {
          case 'upsert':
            if (onGistUpdate && data.gist) {
              onGistUpdate(data.gist)
            }
            break

          case 'delete':
            if (onGistUpdate && data.gist_id) {
              onGistUpdate({ gist_id: data.gist_id, _deleted: true })
            }
            break

          case 'error':
            console.error('[WebSocket] Error:', data.message)
            break

          default:
            console.warn('[WebSocket] Unknown message type:', data.type)
        }
      }
    })
  }, [onGistUpdate, onUnauthorized])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (subscriptionRef.current) {
      console.log('[WebSocket] Disconnecting...')
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }

    if (consumerRef.current) {
      consumerRef.current.disconnect()
      consumerRef.current = null
    }

    isConnectedRef.current = false
    setIsConnected(false)
  }, [])

  const reconnect = useCallback(() => {
    console.log('[WebSocket] Manual reconnect requested')
    isUnauthorizedRef.current = false
    disconnect()
    setTimeout(connect, 100)
  }, [disconnect, connect])

  useEffect(() => {
    connect()

    return () => {
      disconnect()
    }
  }, [])

  return {
    isConnected,
    disconnect,
    reconnect
  }
}
